import { useEffect, useState } from "react";
import PageHeader from "../components/layout/PageHeader";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const EMPTY = {
  code: "", name: "", numbers_required: 1, payout_multiplier: 70,
  order_required: false, min_bet: 5, max_bet: 5000, description: "", active: true,
};

export default function PlayTypes() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => setItems((await api.get("/play-types")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = {
        ...form,
        numbers_required: parseInt(form.numbers_required) || 1,
        payout_multiplier: parseFloat(form.payout_multiplier) || 0,
        min_bet: parseFloat(form.min_bet) || 0,
        max_bet: parseFloat(form.max_bet) || 0,
      };
      if (editing) await api.put(`/play-types/${editing.id}`, payload);
      else await api.post("/play-types", payload);
      toast.success(editing ? "Actualizado" : "Creado");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminar ${p.name}?`)) return;
    try { await api.delete(`/play-types/${p.id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="flex-1 bg-slate-50 min-h-full">
      <PageHeader
        title="Tipos de Jugada"
        subtitle="Reglas de pago y estructura de cada modalidad"
        testId="playtypes-header"
        actions={
          <Button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} className="bg-blue-600 hover:bg-blue-700 rounded-sm font-bold" data-testid="playtypes-create-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuevo tipo
          </Button>
        }
      />
      <div className="p-6 sm:p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((p) => (
            <div key={p.id} className="bg-white border border-slate-200 rounded-sm p-5" data-testid={`playtype-card-${p.code}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 font-mono">{p.code}</div>
                  <div className="text-xl font-black text-slate-950 tracking-tight" style={{ fontFamily: "Manrope" }}>{p.name}</div>
                </div>
                <div className="text-right shrink-0 ml-2">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Pago</div>
                  <div className="text-2xl font-black font-mono text-green-700">{p.payout_multiplier}x</div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs font-mono text-slate-700">
                <div><span className="text-slate-500">Números:</span> <b>{p.numbers_required}</b></div>
                <div><span className="text-slate-500">Orden:</span> <b>{p.order_required ? "Sí" : "No"}</b></div>
                <div><span className="text-slate-500">Min:</span> <b>RD${p.min_bet}</b></div>
                <div><span className="text-slate-500">Max:</span> <b>RD${p.max_bet}</b></div>
              </div>
              {p.description && <div className="mt-3 text-xs text-slate-600">{p.description}</div>}
              <div className="mt-4 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => { setEditing(p); setForm(p); setOpen(true); }} className="h-8 rounded-sm border-slate-300 flex-1" data-testid={`playtype-edit-${p.code}`}><Pencil className="w-3 h-3 mr-1" />Editar</Button>
                <Button size="sm" variant="outline" onClick={() => remove(p)} className="h-8 rounded-sm border-red-300 text-red-700"><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader><DialogTitle>{editing ? "Editar tipo" : "Nuevo tipo de jugada"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Código</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="mt-1 rounded-sm font-mono" data-testid="playtype-form-code" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Nombre</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" data-testid="playtype-form-name" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Números req.</Label>
              <Input type="number" min="1" value={form.numbers_required} onChange={(e) => setForm({ ...form, numbers_required: e.target.value })} className="mt-1 rounded-sm font-mono" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Multiplicador</Label>
              <Input type="number" step="0.1" value={form.payout_multiplier} onChange={(e) => setForm({ ...form, payout_multiplier: e.target.value })} className="mt-1 rounded-sm font-mono" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Min apuesta</Label>
              <Input type="number" step="0.01" value={form.min_bet} onChange={(e) => setForm({ ...form, min_bet: e.target.value })} className="mt-1 rounded-sm font-mono" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Max apuesta</Label>
              <Input type="number" step="0.01" value={form.max_bet} onChange={(e) => setForm({ ...form, max_bet: e.target.value })} className="mt-1 rounded-sm font-mono" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wider">Descripción</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-sm" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.order_required} onCheckedChange={(v) => setForm({ ...form, order_required: v })} />
              <Label className="text-sm">Orden requerido</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <Label className="text-sm">Activo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-700 rounded-sm" data-testid="playtype-form-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
